import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import type {
  DateValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers'
import moment, { Moment } from 'moment'
import { tokens } from 'styles/tokens'

// ENG2-2398 — these mirror the customer-level "Expiration Date" field so both
// date inputs on the page read as one control. Source of truth for the look is
// `general-information/general-information.styles.ts` (fieldLabel / datePicker);
// keep the two in step if that one changes.
const fieldLabel = {
  fontSize: '12px',
  fontWeight: 500,
  color: tokens.colors.grey[600],
  mb: 0.75,
}

const datePicker = {
  '& .MuiOutlinedInput-root': {
    height: '40px',
    fontSize: '14px',
    backgroundColor: 'background.paper',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: 'divider',
    },
    '&:hover fieldset': {
      borderColor: 'text.disabled',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  },
  '& .MuiFormHelperText-root': {
    fontSize: '11px',
    marginLeft: 0,
  },
}

interface CustomDateInputProps {
  value: string | null
  label: string
  isLoading: boolean
  onChange: (isoDate: string | null) => void
  /** Earliest selectable day, ISO `YYYY-MM-DD`. */
  minDate?: string | null
  /** Block days before today. */
  disablePast?: boolean
  helperText?: string
  /** false: no clear button, and an emptied field is never sent (ENG2-2461). */
  allowClear?: boolean
}

function CustomDateInput({
  value,
  label,
  isLoading,
  onChange,
  minDate,
  disablePast,
  helperText,
  allowClear = true,
}: CustomDateInputProps) {
  const handleChange = (
    newMoment: Moment | null,
    context?: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    if (!newMoment) {
      if (allowClear) onChange(null)
      return
    }
    // Every change PUTs immediately, so anything the picker rejects must not
    // reach the server: a half-typed date, or one that breaks minDate /
    // disablePast. The field keeps showing the bad value with its error so the
    // operator can see what to correct.
    if (!newMoment.isValid() || context?.validationError) return
    onChange(newMoment.format('YYYY-MM-DD'))
  }

  return (
    <Box>
      {/* The label sits above the field rather than floating inside it, matching
          Expiration Date. That leaves the input with no <label> of its own, so
          the name is carried by aria-label — the reference field omits this and
          is unlabelled for screen readers; not copying that. */}
      <Typography sx={fieldLabel}>{label}</Typography>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          format="DD.MM.YYYY"
          value={value ? moment(value, 'YYYY-MM-DD') : null}
          onChange={handleChange}
          disabled={isLoading}
          minDate={minDate ? moment(minDate, 'YYYY-MM-DD') : undefined}
          disablePast={disablePast}
          slotProps={{
            textField: {
              variant: 'outlined',
              size: 'small',
              fullWidth: true,
              helperText,
              sx: datePicker,
              inputProps: { 'aria-label': label },
            },
            field: {
              // clearing fires the DatePicker onChange (-> handleChange(null)
              // -> onChange(null)). Do NOT also wire onClear here or the clear
              // produces two onChange(null) calls and thus two PUTs.
              clearable: allowClear,
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  )
}

export default CustomDateInput
